import React, { createContext, useContext, useState } from 'react';

import {initialAppStatus} from "../ctx/AppStatus";
import {pageCfg} from "../ctx/PageSettings";
import {setAccessCtrl, resetAccessCtrl} from "../ctx/AccessCtrl";
import {resetAccessToken} from "../gateway/CallService";

// create application context
const AppContext = createContext();

// application context provider
export function AppCtxProvider({ children }) {

    // app status
    const [appStatus, setAppStatus] = useState(initialAppStatus())

    const updateAppStatus = (key, newStatus) => {
        setAppStatus(prev => ({
            ...prev,
            [key]: newStatus
        }));
    };

    // user and user related functions
    const [user, setUser] = useState({});
    const isLoggedIn = Boolean(user.id);

    // menu settings
    const [currentMenu, setCurrentMenu] = useState('login');
    const [currentData, setCurrentData] = useState(null);
    const [currentReturnTo, setCurrentReturnTo] = useState(null);

    // page settings
    const [pageSettings, setPageSettings] = useState(pageCfg);

    const getPageState = (pageId, dataKey) => pageSettings[pageId][dataKey];

    const setPageState = (pageId, dataKey, dataValue) => {
        setPageSettings((prev) => ({
            ...prev,
            [pageId]: {
                ...prev[pageId],            // preserve existing keys for the page
                [dataKey]: dataValue,       // update or add the specific key
            },
        }));
    };

    const setPageData = (pageId, dataKey, dataValue) => {
        setPageSettings((prev) => ({
            ...prev,
            [pageId]: {
                ...prev[pageId],            // preserve existing keys for the page
                [dataKey]: dataValue,       // update or add the specific key
            },
        }));
    };

    const getPageData = (pageId, dataKey, defaultDataValue = null) => {
        const pageData = pageSettings[pageId] || null; // Return `null` if pageData is not found
        if (!pageData) {
            return defaultDataValue;
        }
        return dataKey ? (pageData[dataKey] ?? defaultDataValue) : pageData;
    };


    // actions
    const logout = () => {
        setUser({});
        setCurrentMenu("login");
        setPageSettings(pageCfg);
        resetAccessToken();
        resetAccessCtrl();
    }

    const login = (user) => {
        setUser(user)
        setCurrentMenu("project")
        setAccessCtrl(user.id, user.role);
        // init page settings
        if (user.role === 'foreman') {
            setPageData('ProjectList', 'foremanFilter', user.id)
            setPageData('ProjectList', 'view', 'planView')
            setPageData('TaskList', 'foremanFilter', user.id)
        }
        if (user.role === 'worker') {
            setCurrentMenu("task")
            setPageData('ProjectList', 'view', 'workView')
            setPageData('TaskList', 'member', true)
            setPageData('TaskList', 'memberFilter', user.id)
            setPageData('TaskList', 'displayOption', 0)
        }
    }

    const handleAction = (action, data= null, returnTo=null) => {
        // set the current data / returnTo (or reset it if not set)
        setCurrentData(data)
        setCurrentReturnTo(returnTo)

        // execute action or set the current menu
        switch (action) {
            case 'userLogout':
                logout();
                break;
            case 'userLogin':
                login(data);
                break;
            default:
                setCurrentMenu(action);
                break;
        }
    };

    return (
        <AppContext.Provider value={{
            appStatus, updateAppStatus, user, setUser, isLoggedIn,
            currentMenu, setCurrentMenu, currentData, setCurrentData, currentReturnTo, setCurrentReturnTo,
            pageSettings, setPageData, getPageData, getPageState, setPageState, handleAction}}>
            {children}
        </AppContext.Provider>
    );
}

// create custom hook to access the application context
export const useAppCtx = () => {
    return useContext(AppContext);
};

